import { motion } from "motion/react";
import { Fragment, type ReactNode } from "react";

// The landing's one entrance: content rises out of a soft blur as it enters
// the viewport, once. Without IntersectionObserver (tests, old browsers)
// motion treats everything as in view, so nothing stays hidden.
const EASE = [0.16, 1, 0.3, 1] as const;
const VIEWPORT = { once: true, amount: 0.2 } as const;

const TAGS = { div: motion.div, p: motion.p, ul: motion.ul, figure: motion.figure } as const;

export function Reveal({
  children,
  as = "div",
  delay = 0,
  y = 28,
  className,
  id,
}: {
  children: ReactNode;
  as?: keyof typeof TAGS;
  delay?: number;
  y?: number;
  className?: string;
  id?: string;
}) {
  const Tag = TAGS[as];
  return (
    <Tag
      id={id}
      className={className}
      initial={{ opacity: 0, y, filter: "blur(10px)" }}
      whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
      viewport={VIEWPORT}
      transition={{ duration: 0.9, delay, ease: EASE }}
    >
      {children}
    </Tag>
  );
}

/** A heading whose words arrive one after another. */
export function RevealWords({
  text,
  as = "h2",
  id,
  className,
  delay = 0,
}: {
  text: string;
  as?: "h2" | "h3";
  id?: string;
  className?: string;
  delay?: number;
}) {
  const Tag = as === "h3" ? motion.h3 : motion.h2;
  const words = text.split(" ");
  return (
    <Tag
      id={id}
      className={className}
      initial="hidden"
      whileInView="shown"
      viewport={VIEWPORT}
      transition={{ staggerChildren: 0.06, delayChildren: delay }}
    >
      {words.map((word, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: a fixed sentence; a word can repeat.
        <Fragment key={`${word}-${index}`}>
          <motion.span
            style={{ display: "inline-block" }}
            variants={{
              hidden: { opacity: 0, y: "0.5em", filter: "blur(8px)" },
              shown: { opacity: 1, y: 0, filter: "blur(0px)" },
            }}
            transition={{ duration: 0.8, ease: EASE }}
          >
            {word}
          </motion.span>
          {index < words.length - 1 ? " " : null}
        </Fragment>
      ))}
    </Tag>
  );
}

/** A product capture that tips up from the page into place. */
export function RevealShot({
  children,
  delay = 0.15,
  className,
}: {
  children: ReactNode;
  delay?: number;
  className?: string;
}) {
  return (
    <motion.div
      className={className}
      style={{ transformOrigin: "50% 100%", transformPerspective: 1400 }}
      initial={{ opacity: 0, y: 70, rotateX: 24, scale: 0.92, filter: "blur(6px)" }}
      whileInView={{ opacity: 1, y: 0, rotateX: 0, scale: 1, filter: "blur(0px)" }}
      viewport={VIEWPORT}
      transition={{ duration: 1.2, delay, ease: EASE }}
    >
      {children}
    </motion.div>
  );
}
